import { IKeyValue } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import { GetDefaultKeyValue } from "source/app/infrastructure/Data/KeyValues/defaultKeyValue";
import { UpdateKeyValueAsync } from "source/app/infrastructure/Data/KeyValues/UpdateKeyValue";
import { IAppContainerState } from "source/app/presentation/AppContainer";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { ReadState } from "source/app/services/UI/State/ReadState";
import { State } from "source/app/services/UI/State/State";

const preferredFallbackVoiceNames = [
    "Siri Male",
    "Siri Voice 1",
    "Daniel",
    "Alex",
    "Bruce",
    "Fred",
    "Oliver",
    "Aaron",
    "Tom",
    "Google UK English Male",
];

export const googleBookOfMindVoiceName = "Google WaveNet (server)";
export const googleBookOfMindVoicePrefix = "Google WaveNet - ";
export const googleBookOfMindAlternatingVoiceName =
    "Google WaveNet - British male (tuned alternation)";
export const googleBookOfMindTunedVoiceNames = [
    "en-GB-Wavenet-D",
    "en-GB-Wavenet-O",
] as const;
export const googleBookOfMindVoiceOptions = [
    "en-US-Wavenet-A",
    "en-US-Wavenet-B",
    "en-US-Wavenet-C",
    "en-US-Wavenet-D",
    "en-US-Wavenet-E",
    "en-US-Wavenet-F",
    "en-US-Wavenet-G",
    "en-US-Wavenet-H",
    "en-US-Wavenet-I",
    "en-US-Wavenet-J",
    "en-GB-Wavenet-A",
    "en-GB-Wavenet-B",
    "en-GB-Wavenet-C",
    "en-GB-Wavenet-D",
    "en-GB-Wavenet-F",
    "en-GB-Wavenet-N",
    "en-GB-Wavenet-O",
];

export interface IGoogleBookOfMindSpeechSettings {
    speakingRate: number;
    pitch: number;
}

const googleBookOfMindVoiceDescriptions: Record<string, string> = {
    "en-GB-Wavenet-D": "British male - rich/deep",
    "en-GB-Wavenet-O": "British male - warm/deep",
};

export function IsGoogleBookOfMindVoice(voiceName: string | undefined) {
    return (
        voiceName === googleBookOfMindVoiceName ||
        voiceName === googleBookOfMindAlternatingVoiceName ||
        voiceName?.startsWith(googleBookOfMindVoicePrefix) === true
    );
}

export function IsGoogleBookOfMindAlternatingVoice(
    voiceName: string | undefined
) {
    return voiceName === googleBookOfMindAlternatingVoiceName;
}

export function GetGoogleBookOfMindVoiceName(voiceName: string | undefined) {
    if (IsGoogleBookOfMindAlternatingVoice(voiceName)) {
        return googleBookOfMindTunedVoiceNames[0];
    }

    if (voiceName === googleBookOfMindVoiceName) {
        return "en-US-Wavenet-D";
    }

    if (voiceName?.startsWith(googleBookOfMindVoicePrefix)) {
        return voiceName.slice(googleBookOfMindVoicePrefix.length);
    }

    return "en-US-Wavenet-D";
}

export function GetGoogleBookOfMindVoiceNameForQuote(
    voiceName: string | undefined,
    quoteIndex?: number
) {
    if (!IsGoogleBookOfMindAlternatingVoice(voiceName)) {
        return GetGoogleBookOfMindVoiceName(voiceName);
    }

    const normalizedQuoteIndex =
        typeof quoteIndex === "number" &&
        Number.isInteger(quoteIndex) &&
        quoteIndex >= 0
            ? quoteIndex
            : 0;

    return googleBookOfMindTunedVoiceNames[
        normalizedQuoteIndex % googleBookOfMindTunedVoiceNames.length
    ];
}

export function GetGoogleBookOfMindVoiceSelection(voiceName: string) {
    return `${googleBookOfMindVoicePrefix}${voiceName}`;
}

export function GetGoogleBookOfMindVoiceDescription(voiceName: string) {
    if (IsGoogleBookOfMindAlternatingVoice(voiceName)) {
        return googleBookOfMindAlternatingVoiceName;
    }

    const description = googleBookOfMindVoiceDescriptions[voiceName];

    return description
        ? `Google WaveNet (${voiceName}) - ${description}`
        : `Google WaveNet (${voiceName})`;
}

export function GetGoogleBookOfMindSpeechSettings(
    voiceName: string
): IGoogleBookOfMindSpeechSettings | undefined {
    if (voiceName === googleBookOfMindTunedVoiceNames[0]) {
        return {
            speakingRate: 1,
            pitch: -1.5,
        };
    }

    if (voiceName === googleBookOfMindTunedVoiceNames[1]) {
        return {
            speakingRate: 0.8,
            pitch: -2.5,
        };
    }

    return undefined;
}

export function GetBookOfMindSavedVoiceName() {
    const savedVoiceName = GetSharedOrPersistedBookOfMindVoiceKeyValue()?.value;
    return savedVoiceName?.trim() || googleBookOfMindAlternatingVoiceName;
}

export function GetBookOfMindVoicePreviewEnabled() {
    return (
        GetSharedOrPersistedBookOfMindVoicePreviewKeyValue()?.value !== "false"
    );
}

export function GetPersistedBookOfMindVoiceKeyValue() {
    const appContainer = State.ReadClass<IAppContainerState>(
        ESharedComponent.AppContainer
    );

    const persistedKeyValue = appContainer?.state.keyValues.find(
        (keyValue) =>
            keyValue.key ===
            ESharedFunction[ESharedFunction.BookOfMind_ttsVoice]
    );

    if (persistedKeyValue) {
        return persistedKeyValue;
    }

    return ReadState<IKeyValue>(ESharedFunction.BookOfMind_ttsVoice);
}

export function GetSharedOrPersistedBookOfMindVoiceKeyValue() {
    return (
        ReadState<IKeyValue>(ESharedFunction.BookOfMind_ttsVoice) ??
        GetPersistedBookOfMindVoiceKeyValue()
    );
}

export function GetDefaultBookOfMindVoicePreviewKeyValue() {
    return {
        ...GetDefaultKeyValue(),
        value: "true",
    } as IKeyValue;
}

export function GetPersistedBookOfMindVoicePreviewKeyValue() {
    const appContainer = State.ReadClass<IAppContainerState>(
        ESharedComponent.AppContainer
    );

    const persistedKeyValue = appContainer?.state.keyValues.find(
        (keyValue) =>
            keyValue.key ===
            ESharedFunction[ESharedFunction.BookOfMind_ttsPreviewEnabled]
    );

    if (persistedKeyValue) {
        return persistedKeyValue;
    }

    return ReadState<IKeyValue>(ESharedFunction.BookOfMind_ttsPreviewEnabled);
}

export function GetSharedOrPersistedBookOfMindVoicePreviewKeyValue() {
    return (
        ReadState<IKeyValue>(ESharedFunction.BookOfMind_ttsPreviewEnabled) ??
        GetPersistedBookOfMindVoicePreviewKeyValue() ??
        GetDefaultBookOfMindVoicePreviewKeyValue()
    );
}

export async function ToggleBookOfMindVoicePreviewAsync() {
    await UpdateKeyValueAsync(
        ESharedFunction.BookOfMind_ttsPreviewEnabled,
        String(!GetBookOfMindVoicePreviewEnabled())
    );
}

export function GetSortedBookOfMindVoices(voices: SpeechSynthesisVoice[]) {
    return [...voices].sort((left, right) =>
        GetVoiceSortKey(left).localeCompare(GetVoiceSortKey(right))
    );
}

export function GetBookOfMindVoiceDescription(voice: SpeechSynthesisVoice) {
    return `${voice.name} (${voice.lang})`;
}

export function WaitForBookOfMindVoicesAsync(
    synth: SpeechSynthesis
): Promise<SpeechSynthesisVoice[]> {
    const voices = synth.getVoices();

    if (voices.length) {
        return Promise.resolve(voices);
    }

    return new Promise((resolve) => {
        const onVoicesChanged = () => {
            cleanup();
            resolve(synth.getVoices());
        };

        const timeoutId = window.setTimeout(() => {
            cleanup();
            resolve(synth.getVoices());
        }, 1000);

        const cleanup = () => {
            window.clearTimeout(timeoutId);
            synth.removeEventListener(
                "voiceschanged",
                onVoicesChanged as EventListener
            );
        };

        synth.addEventListener(
            "voiceschanged",
            onVoicesChanged as EventListener
        );
    });
}

export function ResolveBookOfMindVoice(
    voices: SpeechSynthesisVoice[],
    preferredVoiceName?: string
) {
    const exactMatch = preferredVoiceName
        ? voices.find((voice) => voice.name === preferredVoiceName)
        : undefined;

    if (exactMatch) {
        return exactMatch;
    }

    const englishVoices = voices.filter((voice) =>
        voice.lang.toLowerCase().startsWith("en")
    );

    return (
        FindVoiceByName(voices, preferredFallbackVoiceNames) ??
        englishVoices[0] ??
        voices[0]
    );
}

function FindVoiceByName(
    voices: SpeechSynthesisVoice[],
    preferredNames: string[]
) {
    const lowerCasePreferredNames = preferredNames.map((name) =>
        name.toLowerCase()
    );

    return voices.find((voice) =>
        lowerCasePreferredNames.includes(voice.name.toLowerCase())
    );
}

function GetVoiceSortKey(voice: SpeechSynthesisVoice) {
    return `${voice.lang.toLowerCase()} ${voice.name.toLowerCase()}`;
}
